import React, { useEffect, useState } from "react";
import { Button, Dimmer, Icon, Label, Loader } from "semantic-ui-react";
import CardSettingServiceUX from "./CardSettingServiceUX";
import CardServiceForm from "./CardServiceForm";
import ModSelectServiceKind from "./ModSelectServiceKind";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import SnackMessage from "../common/SnackMessage";
import { formatADtoBEString } from "react-lib/utils/dateUtils";
import { formatComma } from "react-lib/utils/tsUtils";
import { canCreateServicePackage } from "./servicePackageAccess";
import { fieldOptions } from "./serviceKindSchema";
import {
  ActionParams,
  CARD,
  CatalogFilter,
  CatalogState,
  LIMIT,
  RecordData,
  buttonKey,
  isEditable,
  formatServiceEndDate,
} from "./sequence/ServiceCatalog";

type Props = {
  setProp: (key: any, value: any) => any;
  runSequence: (params: any) => any;
  ServiceCatalogSequence?: CatalogState | null;
  selectedDivision?: RecordData;
  buttonLoadCheck?: RecordData;
  errorMessage?: RecordData;
  successMessage?: RecordData;
  controller?: any;
  onEvent?: any;
  loadingStatus?: any;
  userTokenize?: any;
  masterOptions?: any;
  masterData?: any;
  languageUX?: "en" | "th";
};
const initialFilter: CatalogFilter = { active_flag: "ALL", scope: "mine", code: "", name: "" };
const CardSettingService = (props: Props) => {
  const [filter, setFilter] = useState<CatalogFilter>(initialFilter);
  const [selectingKind, setSelectingKind] = useState(false);
  const division = props.selectedDivision?.id ?? props.controller?.data.division;
  const sequence = props.ServiceCatalogSequence;
  const { runSequence } = props;
  useEffect(() => {
    setFilter(initialFilter);
    setSelectingKind(false);
    runSequence({ sequence: "ServiceCatalog", action: "CLOSE_SERVICE_FORM" });
    runSequence({
      sequence: "ServiceCatalog",
      action: "SEARCH_SERVICE",
      restart: true,
      filter: initialFilter,
    });
  }, [division, runSequence]);
  const run = (action: ActionParams["action"], params: Partial<ActionParams> = {}) =>
    runSequence({ sequence: "ServiceCatalog", action, ...params });
  const loading = (action: string) => props.buttonLoadCheck?.[buttonKey(action)] === "LOADING";
  const search = (nextFilter = filter, activePage = 1) =>
    run("SEARCH_SERVICE", { filter: nextFilter, activePage });
  const rows = (sequence?.division === division ? sequence?.serviceList || [] : []).map((item) => {
    const editable = isEditable(item, division);
    return {
      ...item,
      serviceCode: item.code,
      name: (
        <div>
          {item.name}
          {!editable && (
            <div>
              <Label size="tiny">
                {item.of_division == null
                  ? "รายการกลาง"
                  : `แชร์จาก ${
                      item.of_division?.name ||
                      item.of_division_name ||
                      item.of_division?.id ||
                      item.of_division
                    }`}
              </Label>
            </div>
          )}
        </div>
      ),
      productType: item.p_type?.name || "",
      servicePrice: <div style={{ textAlign: "right" }}>{formatComma(item.price)}</div>,
      startDate: formatADtoBEString(item.start_date),
      endDate: formatServiceEndDate(item.end_date),
      serviceStatus: item.active_flag_label,
      action: (
        <Button size="small" onClick={() => run("OPEN_SERVICE_FORM", { item })}>
          <Icon name={editable ? "edit outline" : "eye"} />
          {editable ? "แก้ไขรายการ" : "ดูรายละเอียด"}
        </Button>
      ),
    };
  });
  const statusOptions = [
    { key: "ALL", value: "ALL", text: "ทั้งหมด" },
    ...fieldOptions(
      { key: "active_flag", label: "", input: "select", options: { enum: true } },
      sequence?.listMetadata?.active_flag
    ),
  ];
  return (
    <div style={{ margin: "10px 0 0" }}>
      <SnackMessage
        error={props.errorMessage?.[CARD]}
        success={props.successMessage?.[CARD]}
        onClose={() => {
          props.setProp(`errorMessage.${CARD}`, null);
          props.setProp(`successMessage.${CARD}`, null);
        }}
      />
      <Dimmer active={loading("OPEN_SERVICE_FORM")} inverted>
        <Loader inverted>กำลังโหลดข้อมูล</Loader>
      </Dimmer>
      <ModSelectServiceKind
        open={selectingKind}
        loading={loading("OPEN_SERVICE_FORM")}
        kinds={sequence?.kinds || []}
        onClose={() => {
          setSelectingKind(false);
          run("CLOSE_SERVICE_FORM");
        }}
        onSelect={(kind) => {
          setSelectingKind(false);
          run("OPEN_SERVICE_FORM", { kind });
        }}
      />
      {sequence?.form && (
        <CardServiceForm
          key={sequence.form.key}
          form={sequence.form}
          canCreatePackage={canCreateServicePackage(props.controller)}
          masters={sequence.masters || {}}
          masterErrors={sequence.masterErrors || {}}
          loading={loading("SAVE_SERVICE") || loading("ADD_PRICING")}
          runSequence={runSequence}
          setProp={props.setProp}
        />
      )}
      <CardSettingServiceUX
        code={filter.code}
        name={filter.name}
        activeFlag={filter.active_flag}
        serviceStatusOption={statusOptions}
        scope={filter.scope}
        onChangeData={(key: string, value: any) => {
          const next = { ...filter, [key]: value };
          setFilter(next);
          if (key === "scope") search(next);
        }}
        serviceData={rows}
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            onClick={() => search()}
            paramKey={buttonKey("SEARCH_SERVICE")}
            buttonLoadCheck={props.buttonLoadCheck?.[buttonKey("SEARCH_SERVICE")]}
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid
            size="small"
            title="ค้นหาข้อมูล"
          />
        }
        buttonAddNewService={
          <Button
            fluid
            size="small"
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            onClick={() => {
              setSelectingKind(true);
              run("GET_KINDS");
            }}
          >
            <Icon name="plus" />
            เพิ่มรายการใหม่
          </Button>
        }
        pagination={
          <PaginationShorthand
            activePage={sequence?.activePage || 1}
            total={sequence?.total || 0}
            limit={LIMIT}
            onPageChange={(activePage: number) => search(sequence?.filter || filter, activePage)}
          />
        }
      />
    </div>
  );
};
export default CardSettingService;
